import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  detectPlatform,
  isStandalone,
  guideIdFor,
  canPromptInstall,
  subscribeInstall,
  promptInstall,
  blockedNotificationHelp
} from './utils/installPlatform';
import { GUIDES, GUIDE_ORDER, InstallSteps, NotifySteps } from './components/InstallSteps';
import './InstallPage.css';

/**
 * /install – the always-available version of the install banner: steps for the visitor's own
 * device first, then notifications, then every other device/browser. Safe to share as a link.
 */
export default function InstallPage() {
  const [platform] = useState(detectPlatform);
  const [standalone] = useState(isStandalone);
  const [nativeReady, setNativeReady] = useState(canPromptInstall);
  const [outcome, setOutcome] = useState(null);
  const mine = guideIdFor(platform);
  const permission = typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;

  useEffect(() => subscribeInstall(() => setNativeReady(canPromptInstall())), []);

  return (
    <div className="install-page">
      <h2>📲 Get the Greek Ranking app</h2>
      <p className="install-page-sub">
        Install Greek Ranking on your phone, tablet or computer for one-tap access, a full-screen app
        experience and notifications. It’s free and takes about 20 seconds — no app store needed.
      </p>

      <section className="install-page-card">
        <h3>1 · Install the app {standalone && <span className="install-page-pill">Installed ✓</span>}</h3>
        {standalone ? (
          <p>You’re already using the installed app. 🎉 Continue with step 2.</p>
        ) : (
          <>
            <p className="install-page-for">For your device: <strong>{GUIDES[mine].label}</strong></p>
            {nativeReady && (
              <button type="button" className="install-prompt-cta" onClick={async () => setOutcome(await promptInstall())}>
                Install app
              </button>
            )}
            {outcome === 'accepted' && <p className="install-page-ok">Installed! Look for the Greek Ranking icon.</p>}
            {nativeReady ? <p className="install-note">Button not working? Follow these steps instead:</p> : null}
            {platform.inApp && (
              <p className="install-page-warn">
                You’re inside another app’s browser (Instagram, Facebook, …). Open this page in
                {platform.os === 'ios' ? ' Safari' : ' Chrome'} first — tap ⋯ → <strong>Open in Browser</strong>.
              </p>
            )}
            <InstallSteps guideId={mine} />
          </>
        )}
      </section>

      <section className="install-page-card">
        <h3>2 · Turn on notifications {permission === 'granted' && <span className="install-page-pill">On ✓</span>}</h3>
        <NotifySteps guideId={mine} />
        {permission === 'denied' && (
          <p className="install-page-warn">
            Notifications are currently <strong>blocked</strong> for this site. {blockedNotificationHelp(platform)}
          </p>
        )}
        <Link to="/notifications" className="install-prompt-cta install-page-btn">Go to Notifications</Link>
      </section>

      <section className="install-page-card">
        <h3>Other devices &amp; browsers</h3>
        {GUIDE_ORDER.filter((id) => id !== mine).map((id) => (
          <details key={id} className="install-page-other">
            <summary>{GUIDES[id].label}</summary>
            <InstallSteps guideId={id} />
          </details>
        ))}
      </section>
    </div>
  );
}
